import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Grid, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import { LoadingBar, TableForm } from 'components/Commons';
import cm from '../../helpers/commons';
import exportedJsonData from './usageDetailJsonData.json';

const TableWrapper = styled.div`
  tbody {
    display: block;
    overflow-y: scroll;
    height: 255px;
  }
  tr {
    display: table;
    width: 100%;

    th,
    td {
      width: 20%;
    }
    th.right.aligned {
      padding-right: 22px;
    }
  }
`;

const usageDetailColumns = [
  { header: '날짜', name: 'useDate', textAlign: 'left', width: 120 },
  { header: '결제번호', name: 'payRoomIdx', textAlign: 'left' },
  { header: '사용자명', name: 'userName', textAlign: 'left' },
  { header: '사용처', name: 'storeName', textAlign: 'left' },
  { header: '사용금액', name: 'comPoint', textAlign: 'right' }
];
const userInfoColumns = [
  [
    { header: '사용자 명', name: 'name' },
    { header: '사용자 ID', name: 'signId' },
    { header: '부서', name: 'orgCode' }
  ],
  [
    { header: '사원 번호', name: 'comIdNum' },
    { header: '직위', name: 'position' },
    { header: '직책', name: 'rankPosition' }
  ]
];
const searchOptionColumns = [
  { header: '포인트 정보', name: 'pointType' },
  { header: '시작 기간', name: 'startDate' },
  { header: '종료 기간', name: 'endDate' }
];

const generateColumns = (values) => {
  const thead = usageDetailColumns.map((item) => ({ data: item.header, ...item }));
  const tbody = values && values.map((value) => {
    const row = usageDetailColumns.map((item) => {
      if (item.name === 'useDate') {
        const days = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' };
        const day = moment(value[item.name]).day();
        const useDate = moment(value[item.name]).format(`YYYY-MM-DD (${days[day]}) HH:mm:ss`);
        return { data: useDate, ...item };
      }
      if (item.name === 'comPoint') {
        return { data: value[item.name].toLocaleString(), ...item, fontColor: value[item.name] < 0 && 'red' };
      }
      return { data: value[item.name] || '-', ...item };
    });
    return { row };
  });
  return { thead, tbody };
};

const generateGridColumns = (columns, value) => (
  <Grid.Row>
    {columns.map(({ header, name }) => {
      const data = name === 'orgCode' ? cm.divisionNavi(value[name]) : value[name];
      return (
        <>
          <Grid.Column width={2} className="summary-hd" style={{ padding: '11px 10px 9px', textAlign: 'center' }}>
            {header}
          </Grid.Column>
          <Grid.Column style={{ padding: '11px 10px 9px', textAlign: 'center' }}>{data || '-'}</Grid.Column>
        </>
      );
    })}
  </Grid.Row>
);

const UserSikdaeUsageDetailModal = ({ open, close, date, userSikdaeUsageDetail, searchDate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(false);

  const usageDetail = useMemo(() => {
    setIsLoading(true);
    const {
      content = [],
      user = {},
      sumPoint = 0,
      totalRow = 0
    } = userSikdaeUsageDetail.data || {};
    if (userSikdaeUsageDetail.data && userSikdaeUsageDetail.data.content) {
      setIsLoading(false);
    }
    return { content, user, sumPoint, totalRow };
  }, [userSikdaeUsageDetail, userSikdaeUsageDetail.data]);

  const tableData = useMemo(() => {
    const { thead, tbody } = generateColumns(usageDetail.content);
    const sub = [
      { data: '합계', colSpan: thead.length - 1, textAlign: 'left' },
      { data: (usageDetail.sumPoint || 0).toLocaleString(), textAlign: 'right' }
    ];
    return { header: { main: thead, sub }, body: { main: tbody } };
  }, [usageDetail, open]);

  const gridData = useMemo(() => {
    return {
      ...usageDetail.user,
      pointType: '식대 포인트',
      startDate: searchDate ? moment(searchDate.startDate).format('YYYY-MM-DD') : '-',
      endDate: searchDate ? moment(searchDate.endDate).format('YYYY-MM-DD') : '-'
    };
  }, [usageDetail, searchDate]);

  const handleExcelDownload = async () => {
    setIsDisable(true);
    const newRows = usageDetail.content.map((item) => {
      const days = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' };
      const day = moment(item.useDate).day();
      const useDate = moment(item.useDate).format(`YYYY-MM-DD (${days[day]}) HH:mm:ss`);
      return ['', useDate, item.payRoomIdx, item.userName, item.storeName, item.comPoint];
    });
    const apiExcelDatas = {
      0: {
        customValues: [
          // 사용자 정보
          { address: "C3", value: gridData.name },
          { address: "E3", value: gridData.userId },
          { address: "G3", value: cm.divisionNavi(gridData.orgCode) },
          { address: "C4", value: gridData.comIdNum || '-' },
          { address: "E4", value: gridData.position || '-' },
          { address: "G4", value: gridData.rankPosition || '-' },
          // 검색 조건
          { address: "C7", value: gridData.pointType },
          { address: "E7", value: moment(gridData.startDate).format('YYYY-MM-DD') },
          { address: "G7", value: moment(gridData.endDate).format('YYYY-MM-DD') }
        ],
        startRowNum: 10,
        newRows
      }
    };
    const fileName = `사용자별 식대 사용내역(상세)_${moment().valueOf()}`;
    await handleFileExport(exportedJsonData, apiExcelDatas, fileName);
    setTimeout(() => {
      setIsDisable(() => false);
    }, 1000);
  };

  return (
    <Modal className="modal-large" open={open} onClose={close} style={{ width: 950 }}>
      {isLoading ? <LoadingBar /> : null}
      <Modal.Header style={{ justifyContent: 'space-between', padding: '12px 20px' }}>
        <div>사용자별 식대 사용내역(상세)</div>
        <Button color="green" inverted style={{ padding: '10px 20px 9px' }} onClick={handleExcelDownload} disabled={isDisable}>
          엑셀 다운로드
        </Button>
      </Modal.Header>
      <Modal.Content>
        <div style={{ padding: '4px 0px', fontWeight: 700 }}>사용자 정보</div>
        <Grid celled columns="equal" className="modal-summary">
          {userInfoColumns.map((columns) => generateGridColumns(columns, gridData))}
        </Grid>
        <div style={{ padding: '4px 0px', fontWeight: 700 }}>검색 조건</div>
        <Grid celled columns="equal" className="modal-summary">
          {generateGridColumns(searchOptionColumns, gridData)}
        </Grid>
        <TableWrapper className="table-box">
          <div style={{ padding: '16px 0px 4px', fontWeight: 700 }}>검색 결과 : {usageDetail.totalRow}건</div>
          <TableForm tabledata={tableData} />
        </TableWrapper>
      </Modal.Content>
      <Modal.Actions style={{ padding: '12px 20px' }}>
        <Button color="green" content="확인" style={{ padding: '10px 20px 9px' }} onClick={close} />
      </Modal.Actions>
    </Modal>
  );
};

export default connect((state) => ({
  date: state.commons.date,
  userSikdaeUsageDetail: state.usage.userSikdaeUsageDetail || {}
}))(UserSikdaeUsageDetailModal);
